import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import t from 'react-translate';
import uuid from 'react-uuid';
import { css } from '@emotion/react';

// redux
import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { useAppDispatch } from 'redux/store';

import NvTextArea from 'shared/components/inputs/nv-text-area';
import ClickableContainer from 'components/clickable-container';
import NvIcon from 'shared/components/nv-icon';
import { black, danger, gray3, gray4, gray6, highTide, primary, white } from 'styles/global_defaults/colors';
import NvTooltip from 'shared/components/nv-tooltip';
import { config } from '../../../../../config/pendo.config.json';
import useAudioTranscript from '../hooks/use-audio-transcript';
import useTokenRefresh from '../hooks/use-token-refresh';
import AiConversationContext,
{
  MessageStatus,
  Role,
} from './context';
import streamAIResponse from './stream-ai-response';

const INACTIVITY_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes
const TYPING_IDLE_MS = 1000;

const ChatBar = () => {
  const {
    messages,
    setMessages,
    sessionData,
    setSessionData,
    isTimeout,
    handleExitChat,
    abortControllerRef,
    handleChunk,
    handleError,
  } = useContext(AiConversationContext);

  const { ensureFreshToken } = useTokenRefresh({ sessionData, setSessionData });

  const dispatch = useAppDispatch();
  const [message, setMessage] = useState('');
  const inactivityTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const chatBarRef = useRef<HTMLDivElement>(null);
  const typingIdleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isTyping, setIsTyping] = useState(false);

  const handleLearnerActivity = useCallback(() => {
    // Learner is typing.
    if (!isTyping) {
      setIsTyping(true);
    }

    // Reset the idle timeout since the learner is still typing.
    if (typingIdleTimeoutRef.current) {
      clearTimeout(typingIdleTimeoutRef.current);
    }

    // Learner stopped typing for 1000ms.
    typingIdleTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
    }, TYPING_IDLE_MS);
  }, [isTyping]);


  const handleTranscriptReceived = useCallback((transcript: string) => {
    setMessage(prevMessage => (prevMessage ? `${prevMessage} ${transcript}` : transcript));
    // Focus back to the input after dictation completes and move cursor to end
    setTimeout(() => {
      const textArea = textAreaRef.current;
      if (textArea) {
        textArea.focus();
        // Move cursor to the end of the text
        const { length } = textArea.value;
        textArea.setSelectionRange(length, length);
      }
    }, 0);
  }, []);

  const {
    isNotStarted,
    isRecording,
    isRecordingInProgress,
    handleDictateRecording,
    renderStartRecordingButton,
    renderRecordingControls,
    renderAudioWaveform,
  } = useAudioTranscript({ onTranscriptReceived: handleTranscriptReceived });

  const lastMessage = messages[messages.length - 1];

  /**
   * Restrict user to send message if the last message is still pending
   * or if the last message is from learner and not failed (to enforce turn-taking)
   * or if there are no messages yet (to enforce starting with AI message)
   * or if the message input is empty
   * or if the session is timed out due to inactivity
   */
  const isSentMessageDisabled = lastMessage?.status === MessageStatus.PENDING
    || (lastMessage?.role === Role.LEARNER && lastMessage?.status !== MessageStatus.FAILED)
    || messages.length === 0
    || !message.trim()
    || isTimeout;

  const styles = css`
    /* Hide focus outline when container is focused programmatically for keyboard events */
    &:focus {
      outline: none !important;
    }
    .chat-box {
      border-color: ${isSentMessageDisabled ? gray3 : primary} !important;
      .send-button {
        width: 36px !important;
        height: 36px !important;
        color: ${isSentMessageDisabled ? gray4 : white} !important;
        background-color: ${isSentMessageDisabled ? gray6 : primary} !important;
        &:hover {
          background-color: ${isSentMessageDisabled ? gray6 : highTide} !important;
        }
      }
      .record-actions {
        width: 36px !important;
        height: 36px !important;
        &.hover-text-black:hover {
          color: ${black} !important;
        }
      }
      .dictate-button {
        border: solid 2px;
      }
    }
    .send-message-textarea {
      width: 90%;
      textarea {
        border: none;
        background-color: transparent;
      }
    }
    .send-button {
      width: 10%;
    }
    .audio-waveform-container {
      flex: 1;
      .audio-stream-preview {
        width: 100%;
        height: 40px;
      }
    }
  `;

  const isWaitingForLearnerResponse = (lastMessage?.status === MessageStatus.SENT && lastMessage?.role === Role.AI)
    || lastMessage?.status === MessageStatus.FAILED;

  const clearInactivityTimer = useCallback(() => {
    // Clear any existing timeout first to prevent multiple timeouts
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
      inactivityTimeoutRef.current = null;
    }
  }, []);

  const startInactivityTimer = useCallback(() => {
    clearInactivityTimer();

    inactivityTimeoutRef.current = setTimeout(() => {
      dispatch(addAlertMessage({
        type: AlertMessageType.WARNING,
        header: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.SESSION_ENDED(),
        message: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.INACTIVE_SESSION(),
        customColor: danger,
        closeOnNavigate: true,
        duration: Infinity,
      }));
      // Skip confirmation dialog for timeout since user is inactive
      handleExitChat(true);
    }, INACTIVITY_TIMEOUT_MS);
  }, [clearInactivityTimer, dispatch, handleExitChat]);


  useEffect(() => {
    // Start a timeout to show an alert if there's no activity for 5 minutes from learner
    if (isWaitingForLearnerResponse && !isRecordingInProgress && !isTyping) {
      startInactivityTimer();
    } else {
      clearInactivityTimer();
    }

    return clearInactivityTimer;
  }, [isWaitingForLearnerResponse, isTyping, isRecordingInProgress, startInactivityTimer, clearInactivityTimer]);

  // Cleanup typing timer
  useEffect(() => () => {
    if (typingIdleTimeoutRef.current) {
      clearTimeout(typingIdleTimeoutRef.current);
      typingIdleTimeoutRef.current = null;
    }
  }, []);

  const handleSendMessage = () => {
    if (isSentMessageDisabled) return;

    if (!sessionData.aiApiEndpoint || !sessionData.jwtToken) {
      dispatch(addAlertMessage({
        type: AlertMessageType.WARNING,
        header: t.FORM.OOPS(),
        message: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.SOMETHING_WENT_WRONG(),
        customColor: danger,
      }));
      return;
    }

    const userMessage = message;

    setMessages(prevMessages => [
      ...prevMessages,
      {
        id: uuid(),
        content: userMessage,
        role: Role.LEARNER,
        createdAt: new Date(),
        status: MessageStatus.PENDING,
      },
    ]);
    setMessage('');

    ensureFreshToken().then((jwtToken) => {
      streamAIResponse({
        aiApiEndpoint: sessionData.aiApiEndpoint,
        token: jwtToken,
        userMessage,
        onChunk: handleChunk,
        onError: handleError,
        abortSignal: abortControllerRef.current?.signal,
      }).catch((_error) => {
        // Catch the re-thrown error from streamAIResponse to prevent unhandled promise rejection.
        // handleError was already called by onError callback which shows alert and marks message as FAILED.
      });
    }).catch(() => {
      handleError();
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !isSentMessageDisabled) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Handle Enter key to confirm dictation when recording is in progress.
  // Scoped to the chat bar container to avoid intercepting Enter on elements outside this component.
  const handleRecordingKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Enter' && isRecording) {
      // Don't intercept if an interactive element (like cancel button) is focused
      const target = e.target as HTMLElement;
      if (target.tagName === 'BUTTON' || target.getAttribute('role') === 'button') {
        return;
      }
      e.preventDefault();
      handleDictateRecording();
    }
  }, [isRecording, handleDictateRecording]);

  // Add scoped keyboard listener for recording confirmation
  useEffect(() => {
    const container = chatBarRef.current;
    if (isRecording && container) {
      // Focus the container so it can receive keyboard events
      container.focus();
      container.addEventListener('keydown', handleRecordingKeyDown);
    }
    return () => {
      if (container) {
        container.removeEventListener('keydown', handleRecordingKeyDown);
      }
    };
  }, [isRecording, handleRecordingKeyDown]);

  const renderSendMessageButton = () => (
    <NvTooltip
      enabled={!isSentMessageDisabled}
      text={t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.SEND_MESSAGE()}
    >
      <ClickableContainer
        onClick={handleSendMessage}
        className='d-flex send-button rounded-circle bg-gray-6 justify-content-center mr-2'
        aria-label={t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.SEND_MESSAGE()}
        disabled={isSentMessageDisabled}
        data-qa={config.pendo.activities.aiRolePlay.conversationModal.sendMessage}
      >
        <NvIcon icon='send-message' size='smallest' className='d-flex align-items-center' />
      </ClickableContainer>
    </NvTooltip>
  );

  return (
    <div
      ref={chatBarRef}
      css={styles}
      className='w-100 align-items-center mt-auto py-4'
      tabIndex={-1}
    >
      <div className='d-flex border-bottom pb-3 bg-white chat-box align-items-center'>
        {isRecordingInProgress ? (
          renderAudioWaveform()
        ) : (
          <NvTextArea
            ref={textAreaRef}
            className='send-message-textarea'
            placeholder={t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.SEND_MESSAGE_PLACEHOLDER()}
            value={message}
            onChange={(e) => {
              setMessage(e.target.value);
              handleLearnerActivity();
            }}
            onKeyDown={handleKeyDown}
            disabled={isTimeout}
          />
        )}
        {isNotStarted && renderStartRecordingButton()}
        {isRecordingInProgress && renderRecordingControls()}
        {isNotStarted && renderSendMessageButton()}
      </div>
    </div>
  );
};

export default ChatBar;
